import React, { useEffect, useState } from "react";
import {
    Box,
    Flex,
    Button,
    Table,
    Tbody,
    Td,
    Th,
    Thead,
    Tr,
    Image,
    Text,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalBody,
    ModalCloseButton,
    useDisclosure,
    Skeleton,
    HStack,
    Img
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import ReactPaginate from "react-paginate";
import chevronLeft from "../../assets/images/employee/chevron-left.svg";
import chevronRight from "../../assets/images/employee/chevron-right.svg";
import { designBackgroundApi } from "../../api/designBackground";

const BASE_URL = process.env.REACT_APP_BACKEND_API;

/* -----------------------------------------------------
   IMAGE PREVIEW MODAL (Zoom + Drag)
----------------------------------------------------- */
function ImagePreviewModal({ isOpen, onClose, url }) {
    const [scale, setScale] = React.useState(1);
    const [pos, setPos] = React.useState({ x: 0, y: 0 });
    const [dragging, setDragging] = React.useState(false);
    const [origin, setOrigin] = React.useState({ x: 0, y: 0 });

    const startDrag = (e) => {
        setDragging(true);
        setOrigin({ x: e.clientX - pos.x, y: e.clientY - pos.y });
    };

    const stopDrag = () => setDragging(false);

    const doDrag = (e) => {
        if (!dragging) return;
        setPos({ x: e.clientX - origin.x, y: e.clientY - origin.y });
    };

    const zoom = (e) => {
        e.preventDefault();
        setScale((prev) => Math.min(Math.max(prev + e.deltaY * -0.001, 0.3), 4));
    };

    React.useEffect(() => {
        if (isOpen) {
            setScale(1);
            setPos({ x: 0, y: 0 });
        }
    }, [isOpen]);

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="full">
            <ModalOverlay bg="rgba(0,0,0,0.85)" />

            <ModalContent bg="rgba(0,0,0,0.6)" shadow="none">
                <ModalCloseButton color="white" size="lg" />

                <Flex
                    flex="1"
                    align="center"
                    justify="center"
                    overflow="hidden"
                    onWheel={zoom}
                    onMouseMove={doDrag}
                    onMouseUp={stopDrag}
                    onMouseLeave={stopDrag}
                    cursor={dragging ? "grabbing" : "grab"}
                >
                    <Image
                        src={url}
                        maxH="90%"
                        maxW="90%"
                        draggable={false}
                        onMouseDown={startDrag}
                        style={{
                            transform: `translate(${pos.x}px, ${pos.y}px) scale(${scale})`,
                            transition: dragging ? "none" : "0.15s",
                        }}
                    />
                </Flex>
            </ModalContent>
        </Modal>
    );
}

/* -----------------------------------------------------
   MAIN LIST PAGE
----------------------------------------------------- */
export default function ListPage() {
    const navigate = useNavigate();

    const [list, setList] = useState([]);
    const [page, setPage] = useState(1);
    const [limit] = useState(10);
    const [totalPages, setTotalPages] = useState(1);
    const [loading, setLoading] = useState(false);

    const [selected, setSelected] = useState(null);

    const { isOpen, onOpen, onClose } = useDisclosure();

    // Preview modal
    const [previewUrl, setPreviewUrl] = useState("");
    const {
        isOpen: isPreviewOpen,
        onOpen: openPreview,
        onClose: closePreview,
    } = useDisclosure();

    const loadData = async () => {
        try {
            setLoading(true);
            const res = await designBackgroundApi.list({
                page,
                limit,
            });
            setList(res.items || []);
            setTotalPages(Math.ceil((res?.pagination?.total || 1) / limit));
        } catch (e) {
            console.log(e);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadData();
    }, [page]);

    const deleteItem = async () => {
        try {
            await designBackgroundApi.deleteOne(selected._id);
            onClose();
            loadData();
        } catch (e) {
            console.log(e);
        }
    };

    return (
        <Flex direction="column" p={6} gap={4}>
            <Flex justify="space-between" align="center">
                <Text fontSize="xl" fontWeight="bold">
                    Quản lý Banner
                </Text>

                <Button
                    colorScheme="blue"
                    onClick={() => navigate("/design-background/create/step1")}
                >
                    + Thêm Banner
                </Button>
            </Flex>

            {/* TABLE */}
            <Box border="1px solid #E2E8F0" borderRadius="md" overflow="hidden">
                <Table variant="simple">
                    <Thead bg="gray.100">
                        <Tr>
                            <Th width="40%">Tên</Th>
                            <Th width="30%">Ảnh</Th>
                            <Th width="30%" textAlign="center">
                                Hành động
                            </Th>
                        </Tr>
                    </Thead>

                    <Tbody>
                        {loading ? (
                            [...Array(5)].map((_, i) => (
                                <Tr key={i}>
                                    <Td><Skeleton height="20px" /></Td>
                                    <Td><Skeleton height="60px" /></Td>
                                    <Td><Skeleton height="20px" /></Td>
                                </Tr>
                            ))
                        ) : list.length === 0 ? (
                            <Tr>
                                <Td colSpan={3} textAlign="center" py={10}>
                                    Không có dữ liệu
                                </Td>
                            </Tr>
                        ) : (
                            list.map((item) => (
                                <Tr key={item._id}>
                                    <Td>{item.name}</Td>

                                    <Td>
                                        <Image
                                            src={BASE_URL + "/" + item.url}
                                            alt="preview"
                                            height="100px"
                                            objectFit="contain"
                                            borderRadius="md"
                                            border="1px solid #EEE"
                                            cursor="pointer"
                                            onClick={() => {
                                                setPreviewUrl(BASE_URL + "/" + item.url);
                                                openPreview();
                                            }}
                                        />
                                    </Td>

                                    <Td textAlign="center">
                                        <HStack spacing={3} justify="center">
                                            <Button
                                                colorScheme="yellow"
                                                size="sm"
                                                onClick={() =>
                                                    navigate(`/design-background/edit/${item._id}`)
                                                }
                                            >
                                                Sửa
                                            </Button>
                                            <Button
                                                colorScheme="red"
                                                size="sm"
                                                onClick={() => {
                                                    setSelected(item);
                                                    onOpen();
                                                }}
                                            >
                                                Xóa
                                            </Button>
                                        </HStack>
                                    </Td>
                                </Tr>
                            ))
                        )}
                    </Tbody>
                </Table>
            </Box>

            {/* Pagination */}
            <Flex justify="center" mt={5}>
                <ReactPaginate
                    className="pagination-custom"
                    pageCount={totalPages}
                    onPageChange={(e) => setPage(e.selected + 1)}
                    previousLabel={
                        <Img
                            w={"16px"}
                            minW={"16px"}
                            h={"16px"}
                            src={chevronLeft}
                        />
                    }
                    nextLabel={
                        <Img
                            w={"16px"}
                            minW={"16px"}
                            h={"16px"}
                            src={chevronRight}
                        />
                    }
                />
            </Flex>

            {/* Delete Dialog */}
            <Modal isOpen={isOpen} onClose={onClose} isCentered>
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>Xóa Background</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        Bạn có chắc chắn muốn xóa:
                        <strong> {selected?.name}</strong> ?
                    </ModalBody>
                    <ModalFooter>
                        <Button mr={3} onClick={onClose}>
                            Hủy
                        </Button>
                        <Button colorScheme="red" onClick={deleteItem}>
                            Xóa
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            {/* IMAGE PREVIEW MODAL */}
            <ImagePreviewModal
                isOpen={isPreviewOpen}
                onClose={closePreview}
                url={previewUrl}
            />
        </Flex>
    );
}
